import React, { useRef } from 'react';
import { Input, Modal } from 'antd';
import { Formik } from 'formik';

import { FormField, FormikFieldTouched } from 'components/common';
import { MAX_SYSTEM_NAME_LENGTH, MIN_SYSTEM_NAME_LENGTH, PERMISSION_SCHEME_MODAL_WIDTH } from 'lib/global';
import { useConstantConfig } from 'lib/hooks';
import useI18n from 'lib/hooks/useI18n';
import { nameVerify } from 'lib/regExp';
import { WorkspaceRole as WorkspaceRoleProps } from 'lib/types/models';

import { useRoleType } from '../hooks';

type ModalProps = {
  title: string;
  onClose: () => void;
  onSubmit: (value: WorkspaceRoleProps) => void;
  visible: boolean;
  record?: WorkspaceRoleProps;
};

const CreateModal: React.FC<ModalProps> = props => {
  const RoleType = useRoleType();
  const { visible, onClose, onSubmit, title, record } = props;
  const form = useRef(null);
  const { t } = useI18n();
  const { NAME_LENGTH_ERROR } = useConstantConfig();

  const handleSubmit = value => {
    onSubmit?.(value);
  };

  const validateOnBlur = async e => {
    e.preventDefault();
    const value = e.target?.value;
    const errorName = nameVerify(value);
    if (!value) {
      form.current.setFieldError('name', t('validate.workspace.noEmptyforName'));
    } else if (value.length < MIN_SYSTEM_NAME_LENGTH || value.length > MAX_SYSTEM_NAME_LENGTH) {
      form.current.setFieldError('name', NAME_LENGTH_ERROR);
    } else if (errorName) {
      form.current.setFieldError('name', errorName);
    }
  };

  const validate = () => {
    return form.current.errors;
  };

  return (
    <Formik
      enableReinitialize
      innerRef={form}
      onSubmit={handleSubmit}
      validate={validate}
      initialValues={{ ...record }}
    >
      {({ handleSubmit }) => (
        <Modal
          maskClosable={false}
          width={PERMISSION_SCHEME_MODAL_WIDTH}
          open={visible}
          title={title}
          onOk={() => handleSubmit()}
          onCancel={onClose}
        >
          <FormikFieldTouched />
          <FormField name="name" label={t('global.name')} required>
            {({ field }) => (
              <Input
                maxLength={MAX_SYSTEM_NAME_LENGTH}
                {...field}
                onBlur={validateOnBlur}
                disabled={RoleType.system.key === record.type}
              />
            )}
          </FormField>
          <FormField label={t('global.description')} name="description">
            {({ field }) => <Input.TextArea maxLength={200} {...field} />}
          </FormField>
        </Modal>
      )}
    </Formik>
  );
};

export default CreateModal;
